import * as THREE from 'three';
import { seeded } from '../components/random';

/**
 * The opening network: one point becomes many, the many form a network, the network collapses
 * into a single point and re-forms as a halo around the wordmark.
 *
 * GSAP only tweens the plain `state` object. update() is the sole writer of GPU buffers, so
 * there is never more than one owner of a vertex position.
 */
export interface NetworkState {
  /** Number of nodes lit (fractional values fade the next node in). */
  reveal: number;
  /** 0 = scattered, 1 = organised network. */
  form: number;
  /** 0 = network, 1 = collapsed to the centre. */
  converge: number;
  /** 0 = wherever converge left it, 1 = halo ring around the wordmark. */
  halo: number;
  links: number;
  opacity: number;
}

const NODE_COLOR = new THREE.Color('#d8fff4');
const LINK_COLOR = new THREE.Color('#67f7d2');

export class NetworkField {
  public readonly group = new THREE.Group();
  public readonly state: NetworkState = { reveal: 0, form: 0, converge: 0, halo: 0, links: 0, opacity: 0 };
  public readonly count: number;

  private readonly scattered: Float32Array;
  private readonly formed: Float32Array;
  private readonly haloAngle: Float32Array;
  private readonly haloJitter: Float32Array;
  private readonly phase: Float32Array;
  private readonly edges: Uint16Array;

  private readonly nodeGeometry = new THREE.BufferGeometry();
  private readonly linkGeometry = new THREE.BufferGeometry();
  private readonly nodeMaterial: THREE.PointsMaterial;
  private readonly linkMaterial: THREE.LineBasicMaterial;
  private readonly nodePositions: Float32Array;
  private readonly nodeColors: Float32Array;
  private readonly linkPositions: Float32Array;
  private readonly linkColors: Float32Array;
  private readonly sprite: THREE.CanvasTexture;

  constructor(count = 84) {
    this.count = count;
    const random = seeded(20260923);

    this.scattered = new Float32Array(count * 3);
    this.formed = new Float32Array(count * 3);
    this.haloAngle = new Float32Array(count);
    this.haloJitter = new Float32Array(count * 2);
    this.phase = new Float32Array(count);

    // Node 0 is the single opening point, always at the origin.
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 1; i < count; i += 1) {
      const i3 = i * 3;
      this.scattered[i3] = (random() - 0.5) * 22;
      this.scattered[i3 + 1] = (random() - 0.5) * 12;
      this.scattered[i3 + 2] = -6 + random() * 9;

      const y = 1 - (i / (count - 1)) * 2;
      const radius = Math.sqrt(1 - y * y);
      const theta = golden * i;
      const spread = 0.55 + random() * 0.45;
      this.formed[i3] = Math.cos(theta) * radius * 6.6 * spread;
      this.formed[i3 + 1] = y * 3.3 * spread;
      this.formed[i3 + 2] = Math.sin(theta) * radius * 2.6 * spread;
    }
    for (let i = 0; i < count; i += 1) {
      this.haloAngle[i] = (i / count) * Math.PI * 2 + (random() - 0.5) * 0.12;
      this.haloJitter[i * 2] = 0.86 + random() * 0.28;
      this.haloJitter[i * 2 + 1] = (random() - 0.5) * 1.2;
      this.phase[i] = random() * Math.PI * 2;
    }

    this.edges = this.buildEdges();

    this.nodePositions = new Float32Array(count * 3);
    // RGBA colours: hidden nodes/links must write zero alpha, or they darken the transparent canvas.
    this.nodeColors = new Float32Array(count * 4);
    this.nodeGeometry.setAttribute('position', new THREE.BufferAttribute(this.nodePositions, 3));
    this.nodeGeometry.setAttribute('color', new THREE.BufferAttribute(this.nodeColors, 4));

    this.linkPositions = new Float32Array(this.edges.length * 3);
    this.linkColors = new Float32Array(this.edges.length * 4);
    this.linkGeometry.setAttribute('position', new THREE.BufferAttribute(this.linkPositions, 3));
    this.linkGeometry.setAttribute('color', new THREE.BufferAttribute(this.linkColors, 4));

    this.sprite = makeSprite();
    this.nodeMaterial = new THREE.PointsMaterial({
      size: 0.2,
      map: this.sprite,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    this.linkMaterial = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const nodes = new THREE.Points(this.nodeGeometry, this.nodeMaterial);
    const links = new THREE.LineSegments(this.linkGeometry, this.linkMaterial);
    nodes.frustumCulled = false;
    links.frustumCulled = false;
    this.group.add(links, nodes);
    this.group.visible = false;
  }

  public update(time: number): void {
    const s = this.state;
    this.group.visible = s.opacity > 0.002;
    if (!this.group.visible) return;

    const formEase = smooth(s.form);
    const haloRotation = time * 0.035;

    for (let i = 0; i < this.count; i += 1) {
      const i3 = i * 3;
      const drift = 0.09 * (1 - s.converge);
      let x = lerp(this.scattered[i3], this.formed[i3], formEase) + Math.sin(time * 0.31 + this.phase[i]) * drift;
      let y = lerp(this.scattered[i3 + 1], this.formed[i3 + 1], formEase) + Math.cos(time * 0.27 + this.phase[i]) * drift;
      let z = lerp(this.scattered[i3 + 2], this.formed[i3 + 2], formEase);

      x *= 1 - s.converge;
      y *= 1 - s.converge;
      z *= 1 - s.converge;

      if (s.halo > 0) {
        const angle = this.haloAngle[i] + haloRotation;
        const r = this.haloJitter[i * 2];
        x = lerp(x, Math.cos(angle) * 9 * r, s.halo);
        y = lerp(y, Math.sin(angle) * 3.3 * r, s.halo);
        z = lerp(z, this.haloJitter[i * 2 + 1], s.halo);
      }

      this.nodePositions[i3] = x;
      this.nodePositions[i3 + 1] = y;
      this.nodePositions[i3 + 2] = z;

      let brightness = THREE.MathUtils.clamp(s.reveal - i, 0, 1) * s.opacity;
      // The lone opening point breathes until company arrives.
      if (i === 0) brightness *= 0.78 + Math.sin(time * 1.7) * 0.22 * THREE.MathUtils.clamp(2 - s.reveal, 0, 1);
      else brightness *= 0.62 + Math.sin(time * 0.9 + this.phase[i]) * 0.12;
      const i4 = i * 4;
      this.nodeColors[i4] = NODE_COLOR.r;
      this.nodeColors[i4 + 1] = NODE_COLOR.g;
      this.nodeColors[i4 + 2] = NODE_COLOR.b;
      this.nodeColors[i4 + 3] = brightness;
    }

    for (let e = 0; e < this.edges.length; e += 2) {
      const a = this.edges[e];
      const b = this.edges[e + 1];
      const visible = Math.min(
        THREE.MathUtils.clamp(s.reveal - a, 0, 1),
        THREE.MathUtils.clamp(s.reveal - b, 0, 1),
      );
      // Links belong to the forming network; in the halo they fade so the ring stays clean.
      const strength = visible * s.links * s.opacity * 0.38 * (1 - s.converge * 0.8) * (1 - s.halo * 0.7);
      this.writeLinkVertex(e, a, strength);
      this.writeLinkVertex(e + 1, b, strength);
    }

    this.nodeGeometry.attributes.position.needsUpdate = true;
    this.nodeGeometry.attributes.color.needsUpdate = true;
    this.linkGeometry.attributes.position.needsUpdate = true;
    this.linkGeometry.attributes.color.needsUpdate = true;
  }

  private writeLinkVertex(slot: number, node: number, strength: number): void {
    const p = slot * 3;
    const c = slot * 4;
    const n = node * 3;
    this.linkPositions[p] = this.nodePositions[n];
    this.linkPositions[p + 1] = this.nodePositions[n + 1];
    this.linkPositions[p + 2] = this.nodePositions[n + 2];
    this.linkColors[c] = LINK_COLOR.r;
    this.linkColors[c + 1] = LINK_COLOR.g;
    this.linkColors[c + 2] = LINK_COLOR.b;
    this.linkColors[c + 3] = strength;
  }

  public dispose(): void {
    this.nodeGeometry.dispose();
    this.linkGeometry.dispose();
    this.nodeMaterial.dispose();
    this.linkMaterial.dispose();
    this.sprite.dispose();
  }

  /** Links each node to its two nearest neighbours in the formed layout; node 0 gets four. */
  private buildEdges(): Uint16Array {
    const pairs = new Set<string>();
    const result: number[] = [];
    for (let i = 0; i < this.count; i += 1) {
      const distances: Array<[number, number]> = [];
      for (let j = 0; j < this.count; j += 1) {
        if (i === j) continue;
        const dx = this.formed[i * 3] - this.formed[j * 3];
        const dy = this.formed[i * 3 + 1] - this.formed[j * 3 + 1];
        const dz = this.formed[i * 3 + 2] - this.formed[j * 3 + 2];
        distances.push([dx * dx + dy * dy + dz * dz, j]);
      }
      distances.sort((p, q) => p[0] - q[0]);
      const k = i === 0 ? 4 : 2;
      for (const [, j] of distances.slice(0, k)) {
        const key = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (pairs.has(key)) continue;
        pairs.add(key);
        result.push(i, j);
      }
    }
    return new Uint16Array(result);
  }
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const smooth = (t: number): number => t * t * (3 - 2 * t);

export function makeSprite(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.28, 'rgba(255,255,255,.7)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
